import React, { useRef, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import apiFetch from '../../../utils/apiFetch';
import { HUNTERS_MARK_EFFECT_ID } from '../classFeatures/ranger/huntersMark';

/**
 * The caster's active Hunter's Mark whose marked creature has dropped to 0 HP (or left the map), or null.
 * `entities` is the sheet's token metadata (currentHp per combatant id).
 */
export const findDroppedHuntersMark = (activeEffects, casterId, entities = {}, tokens = {}, effectId = HUNTERS_MARK_EFFECT_ID) => {
  const effects = Array.isArray(activeEffects) ? activeEffects : [];
  const root = effects.find(effect => effect?.definitionId === effectId && !effect.parentEffectId && effect.concentrationId
    && String(effect.sourceCombatantId) === String(casterId));
  if (!root) return null;
  const targetIds = effects.filter(effect => effect.parentEffectId === root.id).map(effect => String(effect.targetCombatantId));
  const known = Object.keys(entities || {}).length > 0;
  const isDown = id => (entities?.[id] ? Number(entities[id].currentHp) <= 0 : known && !tokens?.[id]);
  if (targetIds.length && !targetIds.every(isDown)) return null;
  return { castId: root.concentrationId, targetIds, targetName: entities?.[targetIds[0]]?.label || 'The marked creature' };
};

/**
 * 2024 Hunter's Mark: when the marked creature drops to 0 HP, the Ranger may use a Bonus Action on their turn
 * to move the mark to a new creature, or end the spell.
 */
export default function HuntersMarkMovePrompt({ effectId = HUNTERS_MARK_EFFECT_ID, spellName = "Hunter's Mark", campaignId, characterId, combatState, isActiveTurn, spatialContext, hidden, onMove, onCombatStateReconciled }) {
  const [dismissed, setDismissed] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const lock = useRef(false);
  const inCombat = Number.isInteger(combatState?.activeTurn);
  const ledger = combatState?.turnActionLedger?.combatantId === String(characterId) && combatState.turnActionLedger.turnSequence === combatState.turnSequence
    ? combatState.turnActionLedger : null;
  const dropped = inCombat && isActiveTurn && !ledger?.bonusActionSpent
    ? findDroppedHuntersMark(combatState?.activeEffects, characterId, spatialContext?.entities, spatialContext?.tokens, effectId) : null;
  const turnKey = dropped ? `${dropped.castId}:${combatState.turnSequence}` : null;
  if (!dropped || hidden || dismissed === turnKey) return null;

  const endSpell = async () => {
    if (lock.current) return;
    lock.current = true; setBusy(true); setError('');
    try {
      const response = await apiFetch(`/campaigns/${encodeURIComponent(campaignId)}/buffs/end`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: `end:${dropped.castId}`, castId: dropped.castId, sourceCombatantId: String(characterId) }) });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || `${spellName} could not be ended.`);
      onCombatStateReconciled?.(result.combat);
    } catch (failure) { setError(failure.message); }
    finally { lock.current = false; setBusy(false); }
  };

  return (
    <Modal className="dnd-modal combat-decision-prompt" centered show onHide={() => setDismissed(turnKey)} backdrop="static" keyboard={false}>
      <Modal.Header><Modal.Title>{spellName}</Modal.Title></Modal.Header>
      <Modal.Body>
        <p>{dropped.targetName} dropped to 0 Hit Points.</p>
        <p>Use a Bonus Action to move {spellName === 'Hex' ? 'the curse' : 'the mark'} to a new creature you can see within 90 feet, or end the spell.</p>
        {error && <div className="text-danger mt-3" role="alert">{error}</div>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="link" disabled={busy} onClick={() => setDismissed(turnKey)}>Not this turn</Button>
        <Button variant="secondary" disabled={busy} onClick={endSpell}>End {spellName}</Button>
        <Button disabled={busy} onClick={() => onMove?.(dropped)}>Choose new target</Button>
      </Modal.Footer>
    </Modal>
  );
}
